/** Range slider under the straddle chart (StockMock's bottom strip): a
 * mini overview of the FULL session with a draggable window. Drag inside
 * the window to pan, drag an edge to resize, click outside to jump the
 * window there. All range math lives in straddleMath; the parent owns
 * the range state and slices the main chart's series with it. */

import { useRef, type MouseEvent as ReactMouseEvent, type ReactElement } from "react";
import type { Dispatch, SetStateAction } from "react";
import type { StraddlePoint } from "@/types/market";
import { svgPath, W } from "./driftShared";
import { brushModeAt, jumpRange, movedRange, paddedBounds, type BrushRange } from "./straddleMath";

const WINDOW_FILL = "color-mix(in oklab, var(--accent, #2563eb) 12%, transparent)";
const DIM_FILL = "color-mix(in oklab, var(--surface-2, #e5e7eb) 65%, transparent)";

export function StraddleBrush({
  points,
  range,
  onRange,
}: Readonly<{
  points: readonly StraddlePoint[];
  range: BrushRange;
  onRange: Dispatch<SetStateAction<BrushRange>>;
}>): ReactElement {
  const ref = useRef<HTMLDivElement>(null);
  const fracOf = (clientX: number): number => {
    const rect = ref.current?.getBoundingClientRect();
    if (rect === undefined || rect.width <= 0) return 0;
    const frac = (clientX - rect.left) / rect.width;
    return Number.isFinite(frac) ? Math.min(Math.max(frac, 0), 1) : 0;
  };
  // Mouse events, like every other chart interaction here (jsdom-testable;
  // touch users have the zoom buttons).
  const onMouseDown = (e: ReactMouseEvent<HTMLDivElement>): void => {
    const frac = fracOf(e.clientX);
    const mode = brushModeAt(frac, range);
    const grabbed = mode === "jump" ? jumpRange(range, frac) : range;
    if (mode === "jump") onRange(grabbed);
    // A jump keeps dragging as a pan; the window travels with the cursor.
    const dragMode = mode === "jump" ? "pan" : mode;
    const offset = frac - grabbed.start;
    const move = (ev: MouseEvent): void => {
      const f = fracOf(ev.clientX);
      onRange((prev) => movedRange(dragMode, prev, f, offset));
    };
    const stop = (): void => {
      document.removeEventListener("mousemove", move);
    };
    document.addEventListener("mousemove", move);
    document.addEventListener("mouseup", stop, { once: true });
  };
  return (
    <div
      ref={ref}
      data-testid="straddle-brush"
      className="relative mt-1 h-9 w-full touch-none select-none overflow-hidden rounded border border-line"
      onMouseDown={onMouseDown}
    >
      <OverviewLine points={points} />
      <WindowOverlay range={range} />
    </div>
  );
}

/** The whole session's straddle line, muted, as the backdrop. */
function OverviewLine({ points }: Readonly<{ points: readonly StraddlePoint[] }>): ReactElement {
  const values = points.map((p) => p.straddle);
  return (
    <svg
      viewBox={`0 0 ${String(W)} 180`}
      preserveAspectRatio="none"
      className="h-full w-full"
      aria-hidden="true"
    >
      <path
        d={svgPath(values, paddedBounds(values))}
        fill="none"
        stroke="var(--ink-secondary, #6b7280)"
        strokeWidth={1.5}
        strokeLinejoin="round"
        opacity={0.55}
      />
    </svg>
  );
}

function WindowOverlay({ range }: Readonly<{ range: BrushRange }>): ReactElement {
  const pct = (f: number): string => `${String(f * 100)}%`;
  return (
    <>
      <div
        className="pointer-events-none absolute inset-y-0 left-0"
        style={{ width: pct(range.start), background: DIM_FILL }}
      />
      <div
        className="pointer-events-none absolute inset-y-0 right-0"
        style={{ width: pct(1 - range.end), background: DIM_FILL }}
      />
      <div
        className="absolute inset-y-0 cursor-grab border-x-2"
        style={{
          left: pct(range.start),
          width: pct(range.end - range.start),
          background: WINDOW_FILL,
          borderColor: "var(--accent, #2563eb)",
        }}
      />
      <div
        className="absolute inset-y-0 w-2 -translate-x-1/2 cursor-col-resize"
        style={{ left: pct(range.start) }}
      />
      <div
        className="absolute inset-y-0 w-2 -translate-x-1/2 cursor-col-resize"
        style={{ left: pct(range.end) }}
      />
    </>
  );
}
